import React, { useMemo } from 'react';

import ProductInstantPromoMessage from '../product-instant-promo-message/product-instant-promo-message';

import { useMyAccount } from '../../shared/hooks/hooks';

import { getStrikethroughEntryInstantSavingsInfo } from '../../shared/utils/cart/cart';
import { formatCurrency } from '../../shared/utils/format/format';
import {
  getShouldShowInstantPromotionMessage,
  getStrikethroughInstantSavingsInfo
} from '../../shared/utils/product/product';

import {
  Maybe,
  OrderEntryData,
  ProductData
} from '../../shared/types/graphql-types';

import './stacked-markdown-price.scss';

interface StackedMarkdownPriceProps {
  entry?: OrderEntryData;
  hideInstantPromo?: boolean;
  hidePercentageSaved?: boolean;
  product?: Maybe<ProductData>;
  totalQuantity?: number;
}

function StackedMarkdownPrice(props: StackedMarkdownPriceProps) {
  const {
    entry,
    hideInstantPromo = false,
    hidePercentageSaved = false,
    product,
    totalQuantity
  } = props;

  const { isLoggedIn } = useMyAccount();
  let instantStrikethroughSavingsPerProduct = 0;
  let totalStrikethroughSavings = 0;

  if (entry) {
    ({
      instantStrikethroughSavingsPerProduct = 0,
      totalStrikethroughSavings = 0
    } = getStrikethroughEntryInstantSavingsInfo(
      entry,
      isLoggedIn,
      totalQuantity
    ));
  } else {
    ({
      instantStrikethroughSavingsPerProduct = 0,
      totalStrikethroughSavings = 0
    } = getStrikethroughInstantSavingsInfo(product, isLoggedIn, totalQuantity));
  }

  const { originalPrice, percentageSaved } = useMemo(() => {
    const { promotionData = [] } = entry || {};
    const {
      mapDisplayRule,
      mapPrice,
      potentialPromotions = []
    } = product || {};
    const { instantPromotion, originalRetailValue } =
      (entry ? promotionData : potentialPromotions)?.find(
        promo => promo?.instantPromotion
      ) || {};

    if (
      process.env.__FF_INSTANT_PRICE_STRIKE_THROUGH__ === 'true' &&
      instantPromotion &&
      totalQuantity &&
      totalQuantity >= 4
    ) {
      return {
        originalPrice: instantStrikethroughSavingsPerProduct
          ? Number(originalRetailValue)
          : 0,
        percentageSaved: null
      };
    } else if (!mapPrice || (mapPrice && mapDisplayRule?.mapPriceDisplay)) {
      return {
        originalPrice: Number(product?.markdownPriceData?.originalRetailValue),
        percentageSaved: product?.markdownPriceData?.formattedPercentageSaved
      };
    } else {
      return {
        originalPrice: 0,
        percentageSaved: null
      };
    }
  }, [entry, instantStrikethroughSavingsPerProduct, product, totalQuantity]);

  const showInstantPromotionMessage =
    !hideInstantPromo &&
    getShouldShowInstantPromotionMessage(
      instantStrikethroughSavingsPerProduct,
      totalQuantity
    );

  if ((isNaN(originalPrice) || originalPrice === 0) && !percentageSaved)
    return null;

  return (
    <div className="stacked-markdown-price" styleName="container">
      {showInstantPromotionMessage && (
        <ProductInstantPromoMessage
          instantStrikethroughSavingsPerProduct={
            instantStrikethroughSavingsPerProduct
          }
          showShortDescription
          totalStrikethroughSavings={totalStrikethroughSavings}
        />
      )}
      <div className="stacked-markdown-price__markdown" styleName="markdown">
        {!isNaN(originalPrice) && (
          <div
            className="stacked-markdown-price__original-price"
            styleName="original-price"
          >
            {formatCurrency(originalPrice)}
          </div>
        )}
        {percentageSaved && !hidePercentageSaved && (
          <div styleName="percentage-saved">Save {percentageSaved}</div>
        )}
      </div>
    </div>
  );
}

export default StackedMarkdownPrice;
